import React, { createContext, useContext, useState } from "react";

const LanguageContext = createContext();

const translations = {
  en: {
    // Header
    nav: {
      home: "Home",
      services: "Services",
      materials: "Materials",
      gallery: "Gallery",
      testimonials: "Testimonials",
      about: "About Us",
    },
    header: {
      quote: "Quote",
      requestQuote: "Request Quote",
    },
    // Hero
    hero: {
      overline: "Quality & Experience — Online Upholstery",
      title1: "Comfort,",
      title2: "Style",
      title3: "& Elegance",
      description: "We are ready to be your allies with your upholstery. You choose the colors, we create the masterpiece. Commercial service specialized in booths and furniture.",
      ctaQuote: "Request Quote",
      ctaGallery: "View Our Work",
    },
    // Video
    video: {
      overline: "See Our Work",
      title: "Craftsmanship in Action",
    },
    // Services
    services: {
      overline: "Our Services",
      title: "Excellence in Every Detail",
      residential: {
        title: "Residential Upholstery",
        description: "We transform your home with high-quality upholstery. Sofas, chairs, headboards and more.",
        features: ["Sofas & Armchairs", "Dining Chairs", "Headboards", "Ottomans & Benches", "Decorative Cushions"],
      },
      commercial: {
        title: "Commercial Upholstery",
        description: "Professional solutions for restaurants, hotels, offices and commercial spaces.",
        features: ["Restaurant Booths", "Office Chairs", "Hotel Furniture", "Vehicle Seats", "Acoustic Panels"],
      },
      custom: {
        title: "Custom Design",
        description: "We create unique custom pieces. You choose colors, materials and design.",
        features: ["Premium Materials", "Custom Colors", "Custom Sizes", "Tufting & Textures", "Design Consulting"],
      },
    },
    // Color Selector
    colorSelector: {
      overline: "Material Selector",
      title: "Choose Your Style",
      vinyl: "Vinyl",
      fabric: "Fabric",
      selectedInfo: "Select a color and material to see how it will look in your project. We have a wide range of colors and textures. Contact us to see physical samples.",
      ctaSample: "Request physical sample",
      textures: { liso: "Smooth", terciopelo: "Velvet", lino: "Linen", seda: "Silk", boucle: "Boucle", algodon: "Cotton", texturizado: "Textured" },
      vinylColors: [
        { id: "v1", name: "Classic Black", hex: "#1A1A1A", texture: "Smooth" },
        { id: "v2", name: "Imperial Gold", hex: "#FFD700", texture: "Smooth" },
        { id: "v3", name: "Elegant Silver", hex: "#C0C0C0", texture: "Smooth" },
        { id: "v4", name: "Crimson Red", hex: "#800000", texture: "Smooth" },
        { id: "v5", name: "Ivory White", hex: "#FFFFF0", texture: "Smooth" },
        { id: "v6", name: "Navy Blue", hex: "#1B2A4A", texture: "Smooth" },
        { id: "v7", name: "Chocolate", hex: "#3E2723", texture: "Textured" },
        { id: "v8", name: "Oxford Gray", hex: "#4A4A4A", texture: "Smooth" },
      ],
      fabricColors: [
        { id: "f1", name: "Black Velvet", hex: "#0D0D0D", texture: "Velvet" },
        { id: "f2", name: "Golden Velvet", hex: "#DAA520", texture: "Velvet" },
        { id: "f3", name: "Natural Linen", hex: "#C4B59D", texture: "Linen" },
        { id: "f4", name: "Emerald Velvet", hex: "#2E5B3C", texture: "Velvet" },
        { id: "f5", name: "Champagne Silk", hex: "#F5E6CC", texture: "Silk" },
        { id: "f6", name: "Cream Boucle", hex: "#E8DCC8", texture: "Boucle" },
        { id: "f7", name: "Burgundy Velvet", hex: "#722F37", texture: "Velvet" },
        { id: "f8", name: "Gray Cotton", hex: "#9E9E9E", texture: "Cotton" },
      ],
    },
    // Gallery
    gallery: {
      overline: "Our Portfolio",
      title: "Completed Projects",
      filters: { all: "All", capitone: "Tufted", acanalado: "Channeled", liso: "Smooth" },
      categories: { capitone: "Tufted", acanalado: "Channeled", liso: "Smooth" },
    },
    // Testimonials
    testimonials: {
      overline: "Testimonials",
      title: "What Our Clients Say",
      items: [
        { name: "Maria Rodriguez", role: "Restaurant Owner", text: "Dominican Upholstered completely transformed our restaurant. The booths look spectacular and our clients can't stop complimenting them." },
        { name: "Carlos Mendez", role: "Interior Designer", text: "The quality of work is exceptional. I always recommend Dominican Upholstered to my clients for any upholstery project." },
        { name: "Ana Patricia Gomez", role: "Residential Client", text: "They reupholstered my old sofa and it looks brand new. The attention to detail and material selection was impeccable." },
      ],
    },
    // About
    about: {
      overline: "About Us",
      title: "Dominican Upholstered",
      p1: "We are a company dedicated to commercial and residential upholstery services. We prioritize comfort, coziness and elegance in every environment. We focus on letting the client choose the colors, whether vinyl or fabric, to their taste.",
      p2: "With over 15 years of experience, we have transformed thousands of spaces, from elegant homes to exclusive restaurants. Every project is a work of art created with dedication and premium materials.",
      years: "Years of Experience",
      values: [
        { title: "Excellence", desc: "Premium materials and impeccable finishes in every project we undertake." },
        { title: "Attention to Detail", desc: "Every stitch, every fold, every cut is executed with artisan precision." },
        { title: "Passion for Service", desc: "We work side by side with you to bring your exact vision to life." },
      ],
    },
    // Quote
    quote: {
      overline: "Contact",
      title: "Request Your Quote",
      description: "Fill out the form and our team will contact you within 24 hours with a personalized quote for your project.",
      phone: "Phone",
      whatsapp: "WhatsApp",
      email: "Email",
      location: "Location",
      form: {
        name: "Name *",
        namePlaceholder: "Your full name",
        email: "Email *",
        emailPlaceholder: "email@example.com",
        phone: "Phone *",
        phonePlaceholder: "(000) 000-0000",
        serviceType: "Service Type *",
        selectService: "Select service",
        materialType: "Material Type",
        selectMaterial: "Select material",
        colorPref: "Preferred Color",
        colorPlaceholder: "E.g.: Black, Gold, Red...",
        description: "Project Description *",
        descPlaceholder: "Describe your project, measurements, number of pieces, etc.",
        submit: "Send Quote",
        sending: "Sending...",
        success: "Quote sent successfully. We will contact you soon!",
        error: "Error sending. Please try again.",
        validation: "Please fill in all required fields",
      },
      serviceOptions: [
        { value: "residential", label: "Residential Upholstery" },
        { value: "commercial", label: "Commercial Upholstery" },
        { value: "custom", label: "Custom Design" },
        { value: "automotive", label: "Automotive Upholstery" },
      ],
      materialOptions: [
        { value: "vinyl", label: "Vinyl" },
        { value: "velvet", label: "Velvet" },
        { value: "leather", label: "Leather" },
        { value: "linen", label: "Linen" },
        { value: "cotton", label: "Cotton" },
        { value: "other", label: "Other" },
      ],
    },
    // Footer
    footer: {
      tagline: "Quality & Experience!",
      slogan: "We are ready to be your allies with your upholstery.",
      links: "Links",
      contact: "Contact",
      locationTitle: "Location",
      rights: "All rights reserved.",
    },
    // Stats
    stats: {
      items: [
        { value: "2,000+", label: "Projects Completed" },
        { value: "500+", label: "Satisfied Clients" },
        { value: "15+", label: "Years of Experience" },
        { value: "5.0", label: "Customer Rating" },
      ],
    },
    // Pricing
    pricing: {
      overline: "Our Packages",
      title: "Booth Pricing",
      popular: "Most Popular",
      cta: "Request Quote",
      packages: [
        {
          name: "Standard Booth",
          price: "$100",
          unit: "/ per foot",
          description: "Quality upholstery for commercial booths with standard materials.",
          features: ["Standard vinyl selection", "Smooth or channeled finish", "Custom measurements", "Professional installation", "1 year warranty"],
        },
        {
          name: "Premium Booth",
          price: "$150",
          unit: "/ per foot",
          description: "Premium upholstery with top-quality materials and tufted finish.",
          features: ["Premium vinyl or fabric", "Tufted (capitone) finish", "Custom colors & design", "Professional installation", "2 year warranty", "Free design consultation"],
        },
        {
          name: "Luxury Custom",
          price: "Custom",
          unit: "quote",
          description: "Fully custom design with premium leathers and exclusive finishes.",
          features: ["Genuine leather options", "Diamond tufting & nailheads", "Complex custom shapes", "White glove installation", "3 year warranty", "Full design service"],
        },
      ],
      benefits: ["Competitive pricing with no hidden fees", "Expert craftsmanship by experienced artisans", "Free delivery in the NJ/NY area", "Quality guarantee on all work"],
    },
    // FAQ
    faq: {
      overline: "FAQ",
      title: "Frequently Asked Questions",
      items: [
        { q: "How long does it take to upholster a booth?", a: "Standard booth upholstery takes 3-5 business days from approval. Rush orders can be completed in 1-2 days with an additional fee. Complex custom designs may take 1-2 weeks." },
        { q: "Do you offer free estimates?", a: "Yes! We offer free estimates. You can contact us via WhatsApp, phone, or fill out our online quote form. We'll respond within 24 hours with a detailed quote." },
        { q: "What materials do you work with?", a: "We work with premium vinyl, genuine leather, velvet, linen, cotton, and specialty fabrics. We carry a wide selection of colors and textures. Visit our showroom or request physical samples." },
        { q: "Do you offer delivery and installation?", a: "Yes, we offer delivery and professional installation in the NJ/NY metropolitan area. We also ship nationwide for larger orders." },
        { q: "What is the minimum order?", a: "There is no minimum order. Whether you need a single booth or a full restaurant setup, we're happy to help." },
        { q: "Do you offer warranty on your work?", a: "Absolutely. All our work comes with a minimum 1-year warranty on craftsmanship. Premium and luxury packages include extended warranties." },
      ],
    },
    // Promo
    promo: {
      title: "Ready to Transform Your Space?",
      description: "Get a free estimate today. Quality craftsmanship, competitive prices, and fast turnaround.",
      ctaWhatsapp: "Chat on WhatsApp",
      ctaCall: "Call Now: 973-288-3496",
    },
    // Map
    map: {
      overline: "Visit Us",
      title: "Our Location",
      visitUs: "Visit Our Workshop",
      address: "Address",
      hours: "Business Hours",
      weekdays: "Mon - Fri: 8:00 AM - 6:00 PM",
      saturday: "Sat: 9:00 AM - 3:00 PM",
      sunday: "Sun: Closed",
      serviceArea: "Service Area",
      areas: "New Jersey, New York & Tri-State Area",
      directions: "Get Directions",
    },
  },
  es: {
    nav: {
      home: "Inicio",
      services: "Servicios",
      materials: "Materiales",
      gallery: "Galeria",
      testimonials: "Testimonios",
      about: "Nosotros",
    },
    header: {
      quote: "Cotizar",
      requestQuote: "Solicitar Cotizacion",
    },
    hero: {
      overline: "Calidad & Experiencia — Tapizado Online",
      title1: "Comodidad,",
      title2: "Confort",
      title3: "y Elegancia",
      description: "Estamos listos para ser tus aliados con tus tapizados. Usted escoge los colores, nosotros creamos la obra maestra. Servicio comercial especializado en booths y mobiliario.",
      ctaQuote: "Solicitar Cotizacion",
      ctaGallery: "Ver Nuestros Trabajos",
    },
    video: {
      overline: "Vea Nuestro Trabajo",
      title: "Artesania en Accion",
    },
    services: {
      overline: "Nuestros Servicios",
      title: "Excelencia en Cada Detalle",
      residential: {
        title: "Tapiceria Residencial",
        description: "Transformamos su hogar con tapiceria de alta calidad. Sofas, sillas, cabeceras y mas.",
        features: ["Sofas y Sillones", "Sillas de Comedor", "Cabeceras", "Otomanas y Bancos", "Cojines Decorativos"],
      },
      commercial: {
        title: "Tapiceria Comercial",
        description: "Soluciones profesionales para restaurantes, hoteles, oficinas y espacios comerciales.",
        features: ["Booths de Restaurante", "Sillas de Oficina", "Mobiliario de Hotel", "Asientos de Vehiculos", "Paneles Acusticos"],
      },
      custom: {
        title: "Diseno Personalizado",
        description: "Creamos piezas unicas a medida. Usted escoge colores, materiales y diseno.",
        features: ["Materiales Premium", "Colores Personalizados", "Medidas a la Orden", "Capitone y Texturas", "Asesoria de Diseno"],
      },
    },
    colorSelector: {
      overline: "Selector de Materiales",
      title: "Escoja Su Estilo",
      vinyl: "Vinil",
      fabric: "Tela",
      selectedInfo: "Seleccione un color y material para ver como lucira en su proyecto. Disponemos de una amplia gama de colores y texturas. Contactenos para ver muestras fisicas.",
      ctaSample: "Solicitar muestra fisica",
      textures: { liso: "Liso", terciopelo: "Terciopelo", lino: "Lino", seda: "Seda", boucle: "Boucle", algodon: "Algodon", texturizado: "Texturizado" },
      vinylColors: [
        { id: "v1", name: "Negro Clasico", hex: "#1A1A1A", texture: "Liso" },
        { id: "v2", name: "Dorado Imperial", hex: "#FFD700", texture: "Liso" },
        { id: "v3", name: "Plata Elegante", hex: "#C0C0C0", texture: "Liso" },
        { id: "v4", name: "Rojo Carmesi", hex: "#800000", texture: "Liso" },
        { id: "v5", name: "Blanco Marfil", hex: "#FFFFF0", texture: "Liso" },
        { id: "v6", name: "Azul Marino", hex: "#1B2A4A", texture: "Liso" },
        { id: "v7", name: "Chocolate", hex: "#3E2723", texture: "Texturizado" },
        { id: "v8", name: "Gris Oxford", hex: "#4A4A4A", texture: "Liso" },
      ],
      fabricColors: [
        { id: "f1", name: "Terciopelo Negro", hex: "#0D0D0D", texture: "Terciopelo" },
        { id: "f2", name: "Terciopelo Dorado", hex: "#DAA520", texture: "Terciopelo" },
        { id: "f3", name: "Lino Natural", hex: "#C4B59D", texture: "Lino" },
        { id: "f4", name: "Terciopelo Esmeralda", hex: "#2E5B3C", texture: "Terciopelo" },
        { id: "f5", name: "Seda Champagne", hex: "#F5E6CC", texture: "Seda" },
        { id: "f6", name: "Boucle Crema", hex: "#E8DCC8", texture: "Boucle" },
        { id: "f7", name: "Terciopelo Borgona", hex: "#722F37", texture: "Terciopelo" },
        { id: "f8", name: "Algodon Gris", hex: "#9E9E9E", texture: "Algodon" },
      ],
    },
    gallery: {
      overline: "Nuestro Portafolio",
      title: "Trabajos Realizados",
      filters: { all: "Todos", capitone: "Capitone", acanalado: "Acanalado", liso: "Liso" },
      categories: { capitone: "Capitone", acanalado: "Acanalado", liso: "Liso" },
    },
    testimonials: {
      overline: "Testimonios",
      title: "Lo Que Dicen Nuestros Clientes",
      items: [
        { name: "Maria Rodriguez", role: "Propietaria de Restaurante", text: "Dominican Upholstered transformo completamente nuestro restaurante. Los booths quedaron espectaculares y nuestros clientes no dejan de elogiarlos." },
        { name: "Carlos Mendez", role: "Disenador de Interiores", text: "La calidad del trabajo es excepcional. Siempre recomiendo a Dominican Upholstered a mis clientes para cualquier proyecto de tapiceria." },
        { name: "Ana Patricia Gomez", role: "Cliente Residencial", text: "Retapizaron mi sofa antiguo y quedo como nuevo. La atencion al detalle y la seleccion de materiales fue impecable." },
      ],
    },
    about: {
      overline: "Sobre Nosotros",
      title: "Dominican Upholstered",
      p1: "Somos una empresa dedicada al servicio de la tapiceria comercial y residencial. Priorizamos la comodidad, el confort y la elegancia en todo el entorno. Nos enfocamos en que el cliente escoja los colores, ya sea del vinil o la tela, a su gusto.",
      p2: "Con mas de 15 anos de experiencia, hemos transformado miles de espacios, desde hogares elegantes hasta restaurantes exclusivos. Cada proyecto es una obra de arte creada con dedicacion y materiales de primera calidad.",
      years: "Anos de Experiencia",
      values: [
        { title: "Excelencia", desc: "Materiales premium y acabados impecables en cada proyecto que realizamos." },
        { title: "Atencion al Detalle", desc: "Cada puntada, cada pliegue, cada corte es ejecutado con precision artesanal." },
        { title: "Pasion por el Servicio", desc: "Trabajamos codo a codo con usted para materializar su vision exacta." },
      ],
    },
    quote: {
      overline: "Contacto",
      title: "Solicite Su Cotizacion",
      description: "Complete el formulario y nuestro equipo le contactara en menos de 24 horas con una cotizacion personalizada para su proyecto.",
      phone: "Telefono",
      whatsapp: "WhatsApp",
      email: "Email",
      location: "Ubicacion",
      form: {
        name: "Nombre *",
        namePlaceholder: "Su nombre completo",
        email: "Email *",
        emailPlaceholder: "correo@ejemplo.com",
        phone: "Telefono *",
        phonePlaceholder: "(000) 000-0000",
        serviceType: "Tipo de Servicio *",
        selectService: "Seleccione servicio",
        materialType: "Tipo de Material",
        selectMaterial: "Seleccione material",
        colorPref: "Color Preferido",
        colorPlaceholder: "Ej: Negro, Dorado, Rojo...",
        description: "Descripcion del Proyecto *",
        descPlaceholder: "Describa su proyecto, medidas, cantidad de piezas, etc.",
        submit: "Enviar Cotizacion",
        sending: "Enviando...",
        success: "Cotizacion enviada exitosamente. Le contactaremos pronto!",
        error: "Error al enviar. Intente nuevamente.",
        validation: "Por favor complete todos los campos requeridos",
      },
      serviceOptions: [
        { value: "residential", label: "Tapiceria Residencial" },
        { value: "commercial", label: "Tapiceria Comercial" },
        { value: "custom", label: "Diseno Personalizado" },
        { value: "automotive", label: "Tapiceria Automotriz" },
      ],
      materialOptions: [
        { value: "vinyl", label: "Vinil" },
        { value: "velvet", label: "Terciopelo" },
        { value: "leather", label: "Cuero" },
        { value: "linen", label: "Lino" },
        { value: "cotton", label: "Algodon" },
        { value: "other", label: "Otro" },
      ],
    },
    footer: {
      tagline: "Calidad & Experiencia!",
      slogan: "Estamos listos para ser tus aliados con tus tapizados.",
      links: "Enlaces",
      contact: "Contacto",
      locationTitle: "Ubicacion",
      rights: "Todos los derechos reservados.",
    },
    stats: {
      items: [
        { value: "2,000+", label: "Proyectos Completados" },
        { value: "500+", label: "Clientes Satisfechos" },
        { value: "15+", label: "Anos de Experiencia" },
        { value: "5.0", label: "Calificacion de Clientes" },
      ],
    },
    pricing: {
      overline: "Nuestros Paquetes",
      title: "Precios de Booths",
      popular: "Mas Popular",
      cta: "Solicitar Cotizacion",
      packages: [
        {
          name: "Booth Estandar",
          price: "$100",
          unit: "/ por pie",
          description: "Tapizado de calidad para booths comerciales con materiales estandar.",
          features: ["Seleccion de vinil estandar", "Acabado liso o acanalado", "Medidas personalizadas", "Instalacion profesional", "1 ano de garantia"],
        },
        {
          name: "Booth Premium",
          price: "$150",
          unit: "/ por pie",
          description: "Tapizado premium con materiales de primera y acabado capitone.",
          features: ["Vinil o tela premium", "Acabado capitone", "Colores y diseno a eleccion", "Instalacion profesional", "2 anos de garantia", "Consultoria de diseno gratis"],
        },
        {
          name: "Lujo Personalizado",
          price: "A Cotizar",
          unit: "",
          description: "Diseno totalmente personalizado con cueros premium y acabados exclusivos.",
          features: ["Opciones de cuero genuino", "Capitone diamante y tachuelas", "Formas personalizadas complejas", "Instalacion premium", "3 anos de garantia", "Servicio de diseno completo"],
        },
      ],
      benefits: ["Precios competitivos sin cargos ocultos", "Artesania experta por profesionales con experiencia", "Entrega gratuita en el area NJ/NY", "Garantia de calidad en todos los trabajos"],
    },
    faq: {
      overline: "Preguntas Frecuentes",
      title: "Preguntas Frecuentes",
      items: [
        { q: "Cuanto tiempo toma tapizar un booth?", a: "Un booth estandar toma 3-5 dias habiles desde la aprobacion. Ordenes urgentes pueden completarse en 1-2 dias con cargo adicional. Disenos personalizados complejos pueden tomar 1-2 semanas." },
        { q: "Ofrecen estimados gratuitos?", a: "Si! Ofrecemos estimados gratuitos. Puede contactarnos por WhatsApp, telefono, o llenando nuestro formulario en linea. Respondemos en menos de 24 horas con una cotizacion detallada." },
        { q: "Con que materiales trabajan?", a: "Trabajamos con vinil premium, cuero genuino, terciopelo, lino, algodon y telas especiales. Tenemos una amplia seleccion de colores y texturas. Visite nuestro taller o solicite muestras fisicas." },
        { q: "Ofrecen entrega e instalacion?", a: "Si, ofrecemos entrega e instalacion profesional en el area metropolitana NJ/NY. Tambien hacemos envios a nivel nacional para ordenes grandes." },
        { q: "Cual es la orden minima?", a: "No hay orden minima. Ya sea que necesite un solo booth o el montaje completo de un restaurante, estamos para ayudarle." },
        { q: "Ofrecen garantia en su trabajo?", a: "Absolutamente. Todos nuestros trabajos incluyen minimo 1 ano de garantia en mano de obra. Los paquetes Premium y Lujo incluyen garantias extendidas." },
      ],
    },
    promo: {
      title: "Listo Para Transformar Su Espacio?",
      description: "Obtenga un estimado gratuito hoy. Artesania de calidad, precios competitivos y entrega rapida.",
      ctaWhatsapp: "Chatear por WhatsApp",
      ctaCall: "Llamar: 973-288-3496",
    },
    map: {
      overline: "Visitenos",
      title: "Nuestra Ubicacion",
      visitUs: "Visite Nuestro Taller",
      address: "Direccion",
      hours: "Horario de Atencion",
      weekdays: "Lun - Vie: 8:00 AM - 6:00 PM",
      saturday: "Sab: 9:00 AM - 3:00 PM",
      sunday: "Dom: Cerrado",
      serviceArea: "Area de Servicio",
      areas: "New Jersey, New York y Area Tri-Estatal",
      directions: "Como Llegar",
    },
  },
};

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState("en");
  const t = translations[lang];
  const toggleLang = () => setLang((prev) => (prev === "en" ? "es" : "en"));

  return (
    <LanguageContext.Provider value={{ lang, t, toggleLang }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext);
}
